import {
  LeftArrowIcon,
  ProgressCircleV3,
  RightArrowIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';

import type { PopHighlightsFeatureData } from '@/app/[locale]/product/rings/oura-gen3/_components/PopHighlights/data/popHighlightsFeatures';
import Motion from '@/app/components/Motion';

import { PopHighlightsFeature } from './PopHighlightsFeature';

export interface PopHighlightsFeatureCarouselProps {
  readonly activeFeatureIndex: number;
  readonly features: PopHighlightsFeatureData[];
  readonly onSlideChange: Dispatch<SetStateAction<number>>;
}

export function PopHighlightsFeatureCarousel({
  activeFeatureIndex,
  features,
  onSlideChange,
}: PopHighlightsFeatureCarouselProps): JSX.Element {
  const t = useTranslations();
  const [wrapperHeight, setWrapperHeight] = useState<number>(0);
  const resizeObserverRef = useRef<ResizeObserver>();

  const observeActiveSlideNode = (node: HTMLDivElement | null) => {
    if (node) {
      // Cleanup previous observers
      resizeObserverRef.current?.disconnect();

      resizeObserverRef.current = new window.ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        setWrapperHeight(rect?.height ?? 0);
      });

      resizeObserverRef.current.observe(node);
    }
  };

  const handlePreviousClick = useCallback(() => {
    onSlideChange((currentIndex) => Math.max(currentIndex - 1, 0));
  }, [onSlideChange]);

  const handleNextClick = useCallback(() => {
    onSlideChange((currentIndex) =>
      Math.min(features.length - 1, currentIndex + 1),
    );
  }, [features, onSlideChange]);

  const isFirst = activeFeatureIndex === 0;
  const isLast = activeFeatureIndex === features.length - 1;

  return (
    <div aria-roledescription="carousel" className="flex flex-col items-end">
      <Motion
        animate={{ height: wrapperHeight }}
        aria-live="polite"
        className="relative w-full flex-none"
        data-cy="pop_highlights_carousel_wrapper"
        style={{ height: wrapperHeight }}
        transition={{ duration: 1 }}
      >
        <AnimatePresence>
          {features.map(
            (feature, i, arr) =>
              activeFeatureIndex === i && (
                <Motion
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-0"
                  data-cy="pop_highlights_feature_carousel_slide"
                  initial={{ opacity: 0, y: 100 }}
                  exit={{
                    opacity: 0,
                    y: -100,
                    transition: { delay: 0, duration: 0.75 },
                  }}
                  key={feature.id}
                  transition={{ delay: 0.25, duration: 0.75 }}
                >
                  <PopHighlightsFeature
                    aria-hidden={activeFeatureIndex !== i}
                    aria-label={t('slider_item_aria_label', {
                      maxNumber: arr.length,
                      number: i + 1,
                    })}
                    aria-roledescription="slide"
                    ref={observeActiveSlideNode}
                    role="group"
                    {...feature}
                  />
                </Motion>
              ),
          )}
        </AnimatePresence>
      </Motion>
      <Motion
        initial={{ opacity: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        viewport={{ once: true }}
        whileInView={{ opacity: 1 }}
      >
        <div className="relative z-10 mt-10 flex items-center gap-x-9 text-sandstone-500">
          <div className="flex">
            <ProgressCircleV3
              className="mr-4"
              current={activeFeatureIndex + 1}
              total={features.length}
            />
            <div aria-hidden="true" className="whitespace-nowrap">
              <TypographyV3 Element="span" color="inherit">
                {`${activeFeatureIndex + 1}/${features.length}`}
              </TypographyV3>
            </div>
          </div>
          <div className="flex gap-x-4">
            <button
              aria-label={t('previous_slide_aria_label')}
              className={cx('flex size-10 items-center justify-center p-0', {
                'cursor-not-allowed': isFirst,
                'opacity-30': isFirst,
              })}
              data-cy="pop_highlights_feature_carousel_prev"
              disabled={isFirst}
              onClick={handlePreviousClick}
              type="button"
            >
              <LeftArrowIcon />
            </button>
            <button
              aria-label={t('next_slide_aria_label')}
              className={cx('flex size-10 items-center justify-center p-0', {
                'cursor-not-allowed': isLast,
                'opacity-30': isLast,
              })}
              data-cy="pop_highlights_feature_carousel_next"
              disabled={isLast}
              onClick={handleNextClick}
              type="button"
            >
              <RightArrowIcon />
            </button>
          </div>
        </div>
      </Motion>
    </div>
  );
}
